import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  getGoals,
  createGoal as createGoalApi,
  updateGoal as updateGoalApi,
  deleteGoal as deleteGoalApi,
  createGoalVersion as createGoalVersionApi,
  getGoal,
  getGoalConditionsSchema,
} from 'shared/api/api';
import { Goal, GoalGraph, GoalGraphNode, GoalGraphNodeKind, GoalMode } from 'types/goal';
import { compareDesc } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { AppState } from './reducers';
import {
  BusinessRulesConditions,
  BusinessRulesConditionsSchema,
} from 'sb-lib/types/business-rules-types';
import { RejectError } from 'shared/api/axios';
import { makeLog } from 'shared/utils/log';
import { message } from 'antd';

const log = makeLog('Goal');

export enum SidebarMode {
  SELECT = 'select',
  EDIT = 'edit',
  CREATE = 'create',
}

export enum GoalBusinessRulesStep {
  overview = 'overview',
  property = 'property',
  condition = 'condition',
}

export const initialState = {
  goal: null as Goal | null,
  goals: [] as Goal[],
  loading: false,
  entrySidebarOpen: false,
  sidebarOpen: false,
  sidebarMode: SidebarMode.SELECT,
  focusedNodeId: null as string | null,
  focusedConditionIndex: null as number | null,
  draftNode: null as Partial<GoalGraphNode> | null,
  hasChanged: false,
  conditionsSchema: null as BusinessRulesConditionsSchema | null,
  businessRulesStep: GoalBusinessRulesStep.overview as GoalBusinessRulesStep,
  focusedRouteTrue: false,
  focusedRouteFalse: false,
};

export type GoalState = typeof initialState;

export const findNode = (
  current: GoalGraphNode,
  focusedNodeId: string | null,
): GoalGraphNode | null => {
  if (current.id === focusedNodeId) return current;

  for (const child of current.children || []) {
    const found = findNode(child, focusedNodeId);
    if (found) return found;
  }

  return null;
};

// Helper function to clean up references in branch/qualify/route_for_chat nodes
const cleanupReferences = (node: GoalGraphNode, nodeIdToRemove: string) => {
  if (node.kind === GoalGraphNodeKind.BRANCH || node.kind === GoalGraphNodeKind.QUALIFY) {
    // Clean up condition childNodeIds
    if (node.conditions) {
      node.conditions.forEach(condition => {
        if (condition.childNodeId === nodeIdToRemove) {
          condition.childNodeId = undefined;
        }
      });
    }
    // Clean up defaultChildNodeId
    if (node.defaultChildNodeId === nodeIdToRemove) {
      node.defaultChildNodeId = undefined;
    }
  } else if (node.kind === GoalGraphNodeKind.ROUTE_FOR_CHAT) {
    // Clean up trueChildNodeId and falseChildNodeId
    if (node.trueChildNodeId === nodeIdToRemove) {
      node.trueChildNodeId = null;
    }
    if (node.falseChildNodeId === nodeIdToRemove) {
      node.falseChildNodeId = null;
    }
  }
  // Recursively check children
  if (node.children) {
    node.children.forEach(child => cleanupReferences(child, nodeIdToRemove));
  }
};

export const fetchGoals = createAsyncThunk(
  'goal/fetchGoals',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getGoals();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const createGoal = createAsyncThunk(
  'goal/createGoal',
  async (
    data: { goal: Partial<Goal>; initialGraph?: GoalGraph },
    { rejectWithValue },
  ) => {
    try {
      log.info('Creating new goal', { goalData: data.goal });
      const goalResponse = await createGoalApi(data.goal);
      const newGoal = goalResponse.data;

      log.info('Creating initial goal version', { goalId: newGoal.id });
      const versionResponse = await createGoalVersionApi(newGoal.id, {
        graph: data.initialGraph ?? {
          root: {
            kind: GoalGraphNodeKind.ACTIVATE_MODE,
            mode: GoalMode.SALES,
            children: [],
          },
        },
      });

      return {
        ...newGoal,
        latestVersion: versionResponse.data,
      };
    } catch (error) {
      log.error('Failed to create goal', { error });
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateGoal = createAsyncThunk(
  'goal/updateGoal',
  async (
    {
      id,
      goal,
      graph,
    }: {
      id: number;
      goal: Partial<Goal>;
      graph?: GoalGraph;
    },
    { rejectWithValue },
  ) => {
    try {
      log.info('Updating goal', { goalId: id, updates: goal });
      const { data } = await updateGoalApi(id, goal);

      if (graph) {
        log.info('Creating new goal version with updated graph', { goalId: id });
        const { data: latestVersion } = await createGoalVersionApi(id, {
          graph,
        });

        return {
          ...data,
          latestVersion,
        };
      }

      return data;
    } catch (error) {
      log.error('Failed to update goal', { goalId: id, error });
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteGoal = createAsyncThunk(
  'goal/deleteGoal',
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteGoalApi(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchGoal = createAsyncThunk(
  'goal/fetchGoal',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getGoal(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const fetchGoalConditionsSchema = createAsyncThunk<
  BusinessRulesConditionsSchema,
  void,
  { state: AppState; rejectValue: RejectError }
>('goal/fetchConditionsSchema', async (_, { rejectWithValue }) => {
  try {
    const response = await getGoalConditionsSchema();
    return response.data;
  } catch (error) {
    return rejectWithValue({
      message: error.message || 'Failed to fetch goal conditions schema',
      statusCode: error.response?.status,
    });
  }
});

const goalSlice = createSlice({
  name: 'goal',
  initialState,
  reducers: {
    setSidebarOpen(state, action: PayloadAction<boolean>) {
      state.sidebarOpen = action.payload;
      state.entrySidebarOpen = false;

      if (!action.payload) {
        state.focusedNodeId = null;
        state.focusedConditionIndex = null;
        state.focusedRouteTrue = false;
        state.focusedRouteFalse = false;
        state.draftNode = null;
        state.businessRulesStep = GoalBusinessRulesStep.overview;
        state.sidebarMode = SidebarMode.SELECT;
      }
    },
    setSidebarMode(state, action: PayloadAction<SidebarMode>) {
      state.sidebarMode = action.payload;
    },
    setEntrySidebarOpen(state, action: PayloadAction<boolean>) {
      state.entrySidebarOpen = action.payload;

      state.sidebarOpen = false;
      state.focusedNodeId = null;
      state.focusedConditionIndex = null;
      state.focusedRouteTrue = false;
      state.focusedRouteFalse = false;
      state.draftNode = null;
      state.businessRulesStep = GoalBusinessRulesStep.overview;
      state.sidebarMode = SidebarMode.SELECT;
    },
    openSelect(
      state,
      action: PayloadAction<
        | {
            parentId?: string;
            conditionIndex?: number;
            routeTrue?: boolean;
            routeFalse?: boolean;
          }
        | undefined
      >,
    ) {
      state.entrySidebarOpen = false;

      state.sidebarOpen = true;
      state.sidebarMode = SidebarMode.SELECT;

      if (action.payload) {
        state.focusedNodeId = action.payload.parentId ?? null;
        state.focusedConditionIndex = action.payload.conditionIndex ?? null;

        state.focusedRouteTrue = action.payload.routeTrue || false;
        state.focusedRouteFalse = action.payload.routeFalse || false;
      }
    },
    openEdit(state, action: PayloadAction<string | undefined>) {
      state.entrySidebarOpen = false;

      state.sidebarOpen = true;
      state.sidebarMode = SidebarMode.EDIT;
      if (action.payload) {
        state.focusedNodeId = action.payload;
      }
    },
    clearDraftNode(state) {
      state.draftNode = null;
    },
    setHasChanged(state, action: PayloadAction<boolean>) {
      state.hasChanged = action.payload;
    },
    setDraftNode(state, action: PayloadAction<GoalGraphNodeKind>) {
      switch (action.payload) {
        case GoalGraphNodeKind.ACTIVATE_MODE:
          state.draftNode = {
            kind: GoalGraphNodeKind.ACTIVATE_MODE,
            mode: GoalMode.SALES,
            children: [],
          };
          break;

        case GoalGraphNodeKind.ALERT:
          state.draftNode = {
            kind: GoalGraphNodeKind.ALERT,
            title: '',
            body: '',
            oncePerSession: false,
            userIds: [],
            userGroupIds: [],
            integrationConfig: {},
            children: [],
          };
          break;

        case GoalGraphNodeKind.BRANCH:
          state.businessRulesStep = GoalBusinessRulesStep.property;
          state.draftNode = {
            kind: GoalGraphNodeKind.BRANCH,
            conditions: [
              {
                conditions: ({
                  all: [],
                } as unknown) as BusinessRulesConditions,
              },
            ],
            children: [],
          };
          break;

        case GoalGraphNodeKind.QUALIFY:
          state.businessRulesStep = GoalBusinessRulesStep.property;
          state.draftNode = {
            kind: GoalGraphNodeKind.QUALIFY,
            conditions: [
              {
                conditions: ({
                  all: [],
                } as unknown) as BusinessRulesConditions,
              },
            ],
            children: [],
          };
          break;

        case GoalGraphNodeKind.CONVERSATION_CONTEXT:
          state.draftNode = {
            kind: GoalGraphNodeKind.CONVERSATION_CONTEXT,
            messages: [{ text: '' }],
            children: [],
          };
          break;

        case GoalGraphNodeKind.SEND_MESSAGE:
          state.draftNode = {
            kind: GoalGraphNodeKind.SEND_MESSAGE,
            children: [],
          };
          break;

        case GoalGraphNodeKind.BOOK_MEETING:
          state.draftNode = {
            kind: GoalGraphNodeKind.BOOK_MEETING,
            calendarEventTypeId: 0,
            message: '',
            children: [],
          };
          break;

        case GoalGraphNodeKind.ROUTE_FOR_CHAT:
          state.draftNode = {
            kind: GoalGraphNodeKind.ROUTE_FOR_CHAT,
            falseChildNodeId: null,
            trueChildNodeId: null,
            waitTime: 0,
            children: [],
          };
          break;

        default:
          state.draftNode = null;
      }
    },
    addGraphNode(
      state,
      action: PayloadAction<{
        node: GoalGraphNode;
      }>,
    ) {
      if (!state.goal?.latestVersion?.graph) return;
      state.hasChanged = true;

      const {
        focusedNodeId,
        focusedConditionIndex,
        focusedRouteTrue,
        focusedRouteFalse,
      } = state;

      const node = { ...action.payload.node };

      // Ensure node has a unique identifier
      if (!node.id) {
        node.id = uuidv4();
      }

      // Case 1: No parent specified - insert at root level
      if (!focusedNodeId) {
        // Preserve existing root as the child of new node
        node.children = [state.goal.latestVersion.graph.root];
        // Set new node as root
        state.goal.latestVersion.graph.root = node;
        return;
      }

      // Case 2: Parent specified - find parent node in tree
      const parentNode = findNode(state.goal.latestVersion.graph.root, focusedNodeId);

      // If parent node found, insert new node
      if (parentNode) {
        if (!parentNode.children) {
          parentNode.children = [];
        }

        // If we have a focusedConditionIndex and parent is a branch/qualify node
        if (
          parentNode.kind === GoalGraphNodeKind.BRANCH ||
          parentNode.kind === GoalGraphNodeKind.QUALIFY
        ) {
          if (focusedConditionIndex !== null) {
            // Set the childNodeId for the specific condition
            if (parentNode.conditions[focusedConditionIndex]) {
              parentNode.conditions[focusedConditionIndex].childNodeId = node.id;
            }
          } else {
            // If no condition index is specified, set as default child
            parentNode.defaultChildNodeId = node.id;
          }
        } else if (parentNode.kind === GoalGraphNodeKind.ROUTE_FOR_CHAT) {
          if (focusedRouteTrue) {
            parentNode.trueChildNodeId = node.id;
          } else if (focusedRouteFalse) {
            parentNode.falseChildNodeId = node.id;
          }
        }

        // Preserve existing children by moving them under new node
        // Only if not a branch/qualify/route_for_chat node
        if (
          parentNode.children.length > 0 &&
          !(
            parentNode.kind === GoalGraphNodeKind.BRANCH ||
            parentNode.kind === GoalGraphNodeKind.QUALIFY ||
            parentNode.kind === GoalGraphNodeKind.ROUTE_FOR_CHAT
          )
        ) {
          node.children = [...parentNode.children];
        }

        // Add the new node to the parent's children
        parentNode.children = [node, ...parentNode.children];
      }
    },
    removeGraphNode(state, action: PayloadAction<string>) {
      if (!state.goal?.latestVersion?.graph) return;
      state.hasChanged = true;

      const nodeIdToRemove = action.payload;
      const root = state.goal.latestVersion.graph.root;

      state.sidebarOpen = false;

      // Clean up any references to the node being removed
      cleanupReferences(root, nodeIdToRemove);

      // Case 1: Removing root node
      if (root.id === nodeIdToRemove) {
        // If root has children, promote first child to root
        if (root.children && root.children.length > 0) {
          log.warn('Removing root node - promoting first child to root', {
            removedNodeId: nodeIdToRemove,
            newRootId: root.children[0].id,
          });
          state.goal.latestVersion.graph.root = root.children[0];
        } else {
          // Root has no children, can't remove the last node
          log.error('Attempted to remove last node in graph', { nodeId: nodeIdToRemove });
          message.warn('At the moment cannot remove the last node in the graph');
          return;
        }
        return;
      }

      // Case 2: Removing non-root node
      const findParentNode = (
        current: GoalGraphNode,
        targetId: string,
      ): GoalGraphNode | null => {
        if (!current.children) return null;

        for (const child of current.children) {
          if (child.id === targetId) return current;
          const found = findParentNode(child, targetId);
          if (found) return found;
        }
        return null;
      };

      const parentNode = findParentNode(root, nodeIdToRemove);

      if (parentNode && parentNode.children) {
        const nodeIndex = parentNode.children.findIndex(
          child => child.id === nodeIdToRemove,
        );

        if (nodeIndex !== -1) {
          const nodeToRemove = parentNode.children[nodeIndex];
          parentNode.children.splice(nodeIndex, 1, ...(nodeToRemove.children || []));
        }
      }
    },
    setFocusedNodeId(state, action: PayloadAction<string | null>) {
      state.focusedNodeId = action.payload;
    },
    setConditions(
      state,
      action: PayloadAction<{ conditions: BusinessRulesConditions; index: number }>,
    ) {
      const { conditions, index } = action.payload;

      // Case 1: We're in CREATE mode with a draft node
      if (
        state.draftNode &&
        (state.draftNode.kind === GoalGraphNodeKind.BRANCH ||
          state.draftNode.kind === GoalGraphNodeKind.QUALIFY)
      ) {
        if (!state.draftNode.conditions) {
          state.draftNode.conditions = [];
        }

        state.draftNode.conditions[index ?? 0].conditions = conditions;
        return;
      }

      // Case 2: We're in EDIT mode with an existing node
      if (state.goal?.latestVersion?.graph && state.focusedNodeId) {
        state.hasChanged = true;

        const nodeToUpdate = findNode(
          state.goal.latestVersion.graph.root,
          state.focusedNodeId,
        );

        if (
          nodeToUpdate &&
          (nodeToUpdate.kind === GoalGraphNodeKind.BRANCH ||
            nodeToUpdate.kind === GoalGraphNodeKind.QUALIFY)
        ) {
          nodeToUpdate.conditions[index ?? 0].conditions = conditions;
        }
      }
    },
    setBusinessRulesStep(state, action: PayloadAction<GoalBusinessRulesStep>) {
      state.businessRulesStep = action.payload;
    },
    addCondition(state) {
      // Case 1: We're in CREATE mode with a draft node
      if (
        state.draftNode &&
        (state.draftNode.kind === GoalGraphNodeKind.BRANCH ||
          state.draftNode.kind === GoalGraphNodeKind.QUALIFY)
      ) {
        if (!state.draftNode.conditions) {
          state.draftNode.conditions = [];
        }

        state.draftNode.conditions.push({
          conditions: ({ all: [] } as unknown) as BusinessRulesConditions,
        });
        return;
      }

      // Case 2: We're in EDIT mode with an existing node
      if (state.goal?.latestVersion?.graph && state.focusedNodeId) {
        state.hasChanged = true;

        const nodeToUpdate = findNode(
          state.goal.latestVersion.graph.root,
          state.focusedNodeId,
        );

        if (
          nodeToUpdate &&
          (nodeToUpdate.kind === GoalGraphNodeKind.BRANCH ||
            nodeToUpdate.kind === GoalGraphNodeKind.QUALIFY)
        ) {
          nodeToUpdate.conditions.push({
            conditions: ({ all: [] } as unknown) as BusinessRulesConditions,
          });
        }
      }
    },
    removeCondition(state, action: PayloadAction<number>) {
      const conditionIndex = action.payload;

      // Case 1: We're in CREATE mode with a draft node
      if (
        state.draftNode &&
        (state.draftNode.kind === GoalGraphNodeKind.BRANCH ||
          state.draftNode.kind === GoalGraphNodeKind.QUALIFY)
      ) {
        if (!state.draftNode.conditions) return;
        state.draftNode.conditions.splice(conditionIndex, 1);
        return;
      }

      // Case 2: We're in EDIT mode with an existing node
      if (state.goal?.latestVersion?.graph && state.focusedNodeId) {
        state.hasChanged = true;

        const nodeToUpdate = findNode(
          state.goal.latestVersion.graph.root,
          state.focusedNodeId,
        );

        if (
          nodeToUpdate &&
          (nodeToUpdate.kind === GoalGraphNodeKind.BRANCH ||
            nodeToUpdate.kind === GoalGraphNodeKind.QUALIFY)
        ) {
          // Get the condition we're about to remove to clean up its node reference
          const conditionToRemove = nodeToUpdate.conditions[conditionIndex];
          if (conditionToRemove.childNodeId) {
            // Clean up the node reference in the graph
            cleanupReferences(
              state.goal.latestVersion.graph.root,
              conditionToRemove.childNodeId,
            );
          }

          // Remove the condition
          nodeToUpdate.conditions.splice(conditionIndex, 1);
        }
      }
    },
    updateGraphNode(
      state,
      action: PayloadAction<{
        updates: Partial<GoalGraphNode>;
      }>,
    ) {
      const { updates } = action.payload;

      // Case 1: We're in CREATE mode with a draft node
      if (state.draftNode) {
        log.info('Updating draft node', { updates });
        state.draftNode = {
          ...state.draftNode,
          ...updates,
        };
        return;
      }

      // Case 2: We're in EDIT mode with an existing node
      if (state.goal?.latestVersion?.graph && state.focusedNodeId) {
        state.hasChanged = true;

        const nodeToUpdate = findNode(
          state.goal.latestVersion.graph.root,
          state.focusedNodeId,
        );

        if (!nodeToUpdate) {
          log.error('Failed to find node to update', {
            focusedNodeId: state.focusedNodeId,
            updates,
          });
          return;
        }

        log.info('Updating existing node', {
          nodeId: nodeToUpdate.id,
          nodeKind: nodeToUpdate.kind,
          updates,
        });

        Object.assign(nodeToUpdate, {
          ...nodeToUpdate,
          ...updates,
          kind: nodeToUpdate.kind,
          children: nodeToUpdate.children,
        });
      }
    },
    setEntryConditions(state, action: PayloadAction<BusinessRulesConditions>) {
      if (state.goal?.latestVersion) {
        state.goal.latestVersion.conditions = action.payload;
        state.hasChanged = true;
      }
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchGoals.pending, state => {
        state.loading = true;
      })
      .addCase(fetchGoals.fulfilled, (state, action) => {
        state.loading = false;
        state.goals = action.payload?.filter(goal => goal.active) || [];
      })
      .addCase(fetchGoals.rejected, state => {
        state.loading = false;
      })
      .addCase(createGoal.pending, state => {
        state.loading = true;
      })
      .addCase(createGoal.fulfilled, (state, action) => {
        state.loading = false;
        state.goals.push(action.payload);
        state.goals.sort((a, b) =>
          compareDesc(new Date(a.dateCreated), new Date(b.dateCreated)),
        );
      })
      .addCase(createGoal.rejected, state => {
        state.loading = false;
      })
      .addCase(updateGoal.pending, () => {
        //
      })
      .addCase(updateGoal.fulfilled, (state, action) => {
        state.loading = false;

        // If this goal was set as default, unset any other default goals
        if (action.payload.isDefault) {
          state.goals = state.goals.map(goal =>
            goal.id !== action.payload.id ? { ...goal, isDefault: false } : goal,
          );
        }

        state.goals = state.goals.map(goal =>
          goal.id === action.payload.id ? action.payload : goal,
        );
        state.hasChanged = false;
      })
      .addCase(updateGoal.rejected, () => {
        //
      })
      .addCase(deleteGoal.pending, () => {
        //
      })
      .addCase(deleteGoal.fulfilled, (state, action) => {
        state.goals = state.goals.filter(goal => goal.id !== action.payload);
      })
      .addCase(fetchGoal.pending, state => {
        state.loading = true;
      })
      .addCase(fetchGoal.fulfilled, (state, action) => {
        state.loading = false;
        state.goal = action.payload;
        state.hasChanged = false;
      })
      .addCase(fetchGoal.rejected, state => {
        state.loading = false;
      })
      .addCase(fetchGoalConditionsSchema.pending, () => {
        //
      })
      .addCase(fetchGoalConditionsSchema.fulfilled, (state, action) => {
        state.conditionsSchema = action.payload;
      })
      .addCase(fetchGoalConditionsSchema.rejected, () => {
        //
      });
  },
});

export const {
  setSidebarOpen,
  setSidebarMode,
  clearDraftNode,
  addGraphNode,
  setFocusedNodeId,
  openSelect,
  openEdit,
  setDraftNode,
  setHasChanged,
  removeGraphNode,
  setConditions,
  setBusinessRulesStep,
  addCondition,
  removeCondition,
  updateGraphNode,
  setEntrySidebarOpen,
  setEntryConditions,
} = goalSlice.actions;
export default goalSlice.reducer;
